import { useEffect, useRef } from "react";
import { Capacitor } from "@capacitor/core";
import { useUISlice } from "@/models/useUISlice";
import { useTranslation } from "react-i18next";
import { CloseIcon } from "./Icons";
import { resetMetaThemeColor, setMetaThemeColor } from "@/models/useTheme";

const LoadingSpinner = () => (
  <svg className="animate-spin w-5 h-5" viewBox="0 0 24 24" fill="none">
    <circle
      className="opacity-25"
      cx="12"
      cy="12"
      r="10"
      stroke="currentColor"
      strokeWidth="4"
    />
    <path
      className="opacity-75"
      fill="currentColor"
      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
    />
  </svg>
);

export interface ModalContainerProps {
  title: string;
  onDismiss: () => void;
  onPrimaryButtonPress?: () => void;
  onLeftButtonPress?: () => void;
  primaryButtonText?: string;
  children: React.ReactNode;
  autoFocus?: boolean;
  modalWidth?: number;
  modalMaxWidth?: number;
  statusBarBgColor?: string;
  hideDismissIcon?: boolean;
  subtitle?: string;
  primaryButtonDisabled?: boolean;
  destructive?: boolean;
  contentClassName?: string;
  removeBottomPadding?: boolean;
  cancelText?: string;
  className?: string;
  hideCancel?: boolean;
  disableModalStyle?: boolean;
  shouldPersistBackdropClick?: boolean;
  loading?: boolean;
  fullScreen?: boolean;
  removeHeader?: boolean;
}

const ModalContainer = (props: ModalContainerProps) => {
  const ref = useRef<HTMLDivElement>(null);
  const isMobile = useUISlice((state) => state.device.isMobile);
  const { t } = useTranslation();
  const effectiveIsMobile = props.disableModalStyle ? false : isMobile;

  // Swipe to close logic for iOS
  const contentRef = useRef<HTMLDivElement>(null);
  const dragStartY = useRef(0);
  const isDragging = useRef(false);
  const currentTranslateY = useRef(0);

  const handleTouchStart = (e: React.TouchEvent) => {
    if (props.shouldPersistBackdropClick) return;
    if (Capacitor.getPlatform() !== "ios") return;

    // Check if the user is scrolling content
    let target = e.target as HTMLElement;
    let canScrollUp = false;

    // Traverse up to the contentRef to check for scrollable areas
    while (target && target !== contentRef.current) {
      if (target.scrollHeight > target.clientHeight && target.scrollTop > 0) {
        canScrollUp = true;
        break;
      }
      target = target.parentElement as HTMLElement;
    }

    if (canScrollUp) return;

    isDragging.current = true;
    dragStartY.current = e.touches[0].clientY;

    if (contentRef.current) {
      contentRef.current.style.transition = "none";
    }
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (!isDragging.current || !contentRef.current) return;

    const deltaY = e.touches[0].clientY - dragStartY.current;

    // Only allow dragging down
    if (deltaY > 0) {
      currentTranslateY.current = deltaY;
      contentRef.current.style.transform = `translateY(${deltaY}px)`;
    }
  };

  const handleTouchEnd = () => {
    if (!isDragging.current || !contentRef.current) return;
    isDragging.current = false;

    if (currentTranslateY.current > 100) {
      props.onDismiss();
    } else {
      // Reset
      contentRef.current.style.transition = "transform 0.3s ease-out";
      contentRef.current.style.transform = "";
      currentTranslateY.current = 0;
    }
  };

  // Handle click outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (props.shouldPersistBackdropClick) return;

      if (ref.current && !ref.current.contains(event.target as Node)) {
        props.onDismiss();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [props.shouldPersistBackdropClick, props.onDismiss]);

  // Disable body scroll when modal is open
  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "";
    };
  }, []);

  // Handle escape key
  useEffect(() => {
    if (props.shouldPersistBackdropClick) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        props.onDismiss();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [props.shouldPersistBackdropClick, props.onDismiss]);

  // Update theme-color meta tag
  useEffect(() => {
    if (props.statusBarBgColor) {
      setMetaThemeColor(props.statusBarBgColor);
    } else {
      // Use CSS variable for modal theme color
      const computedColor = getComputedStyle(document.documentElement)
        .getPropertyValue("--theme-color-modal")
        .trim();
      if (computedColor) {
        setMetaThemeColor(computedColor);
      }
    }

    return () => {
      resetMetaThemeColor();
    };
  }, [props.statusBarBgColor]);

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (props.shouldPersistBackdropClick) return;
    if (e.target === e.currentTarget) {
      props.onDismiss();
    }
  };

  return (
    <div
      className={`fixed inset-0 z-[1000] flex ${
        effectiveIsMobile ? "items-end" : "items-center justify-center p-4"
      }`}
      onClick={handleBackdropClick}
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/40 backdrop-blur-sm"
        onClick={props.shouldPersistBackdropClick ? undefined : props.onDismiss}
      />

      {/* Modal wrapper */}
      <div
        ref={ref}
        className={`relative w-full ${effectiveIsMobile ? "" : "flex items-center justify-center"}`}
        style={{
          maxWidth:
            props.fullScreen || effectiveIsMobile
              ? "100%"
              : props.modalMaxWidth
                ? `${props.modalMaxWidth}px`
                : props.modalWidth
                  ? `${props.modalWidth}px`
                  : "500px",
        }}
      >
        {/* Modal content */}
      <div
        ref={contentRef}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        className={`
          w-full bg-[var(--bg-primary)]/90 backdrop-blur-lg shadow-2xl
            ${
              props.fullScreen
                ? "h-screen rounded-none px-6 py-10"
                : effectiveIsMobile
                  ? `rounded-t-[2.5rem] rounded-b-none px-6 pt-8 ${
                      props.removeBottomPadding ? "pb-6" : "pb-24"
                    }`
                  : "rounded-[2.5rem] px-10 py-10"
            }
            ${props.className || ""}
          `}
        >
          {/* Header */}
          {!props.removeHeader && (
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-medium tracking-tight text-[var(--text-primary)] flex-1 mr-4 truncate">
                {props.title}
              </h2>

              {!props.hideDismissIcon && (
                <button
                  onClick={props.onDismiss}
                  className="group relative flex items-center justify-center w-10 h-10 rounded-full bg-[var(--glass-bg)] hover:bg-[var(--glass-bg-active)] transition-all duration-200 backdrop-blur-sm text-[var(--text-primary)]/80 hover:text-[var(--text-primary)]"
                  aria-label={t("modalContainer.closeModal")}
                >
                  <CloseIcon className="w-6 h-6 opacity-70 group-hover:opacity-100 transition-opacity" />
                </button>
              )}
            </div>
          )}

          {/* Subtitle */}
          {props.subtitle && (
            <p className="mb-8 text-base leading-relaxed text-[var(--text-secondary)] font-light">
              {props.subtitle}
            </p>
          )}

          {/* Children content */}
          <div className={`mb-8 ${props.contentClassName || ""}`}>
            {props.children}
          </div>

          {/* Button container */}
          {!props.hideCancel && (
            <div className={`flex items-center gap-4 ${effectiveIsMobile ? "flex-col-reverse" : "justify-end"}`}>
              <button
                onClick={props.onLeftButtonPress || props.onDismiss}
                className={`
                  h-14 px-8 text-base font-medium text-[var(--text-primary)]/80 
                  bg-[var(--glass-bg)] hover:bg-[var(--glass-bg-active)] 
                  rounded-2xl transition-all duration-200
                  ${effectiveIsMobile ? "w-full" : ""}
                `}
              >
                {props.cancelText || t("modalContainer.cancelButton")}
              </button>

              {props.onPrimaryButtonPress && (
                <button
                  onClick={props.onPrimaryButtonPress}
                  disabled={props.primaryButtonDisabled || props.loading}
                  className={`
                    h-14 px-8 text-base font-semibold rounded-2xl transition-all duration-300
                    flex items-center justify-center gap-2
                    ${
                      props.destructive
                        ? "text-white bg-[var(--destructive)] hover:bg-[var(--destructive)]/90"
                        : "text-[var(--text-inverse)] bg-[var(--accent)] hover:bg-[var(--accent)]/90"
                    }
                    ${props.primaryButtonDisabled ? "opacity-50 cursor-not-allowed" : "shadow-lg hover:shadow-xl hover:scale-[1.02] active:scale-[0.98]"}
                    disabled:cursor-not-allowed
                    ${effectiveIsMobile ? "w-full" : ""}
                  `}
                >
                  {props.loading && <LoadingSpinner />}
                  {props.primaryButtonText}
                </button>
              )}
            </div>
          )}

          {/* Single primary button when hideCancel is true */}
          {props.hideCancel && props.onPrimaryButtonPress && (
            <button
              onClick={props.onPrimaryButtonPress}
              disabled={props.primaryButtonDisabled || props.loading}
              className={`
                w-full h-14 px-8 text-base font-semibold rounded-2xl transition-all duration-300
                flex items-center justify-center gap-2
                ${
                  props.destructive
                    ? "text-white bg-[var(--destructive)] hover:bg-[var(--destructive)]/90"
                    : "text-[var(--text-inverse)] bg-[var(--accent)] hover:bg-[var(--accent)]/90"
                }
                ${props.primaryButtonDisabled ? "opacity-50 cursor-not-allowed" : "shadow-lg hover:shadow-xl hover:scale-[1.02] active:scale-[0.98]"}
                disabled:cursor-not-allowed
              `}
            >
              {props.loading && <LoadingSpinner />}
              {props.primaryButtonText}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

ModalContainer.OVERLAY_COLOR = "rgba(0, 0, 0, 0.4)";

export default ModalContainer;
